import {
  Box,
  Button,
  Center,
  Flex,
  Heading,
  Text,
  SkeletonText,
  useDisclosure,
} from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { InfoBlock } from 'src/components/info-block';
import SvgVector from 'src/components/icons/vector';
import { useUpdateRepplMutation } from 'src/app/reppl/hooks';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { Reppl } from '../service';
import Guidelines from './guidelines';

function BriefRepplViewer({
  reppl,
  loading = false,
}: {
  reppl?: Reppl;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <InfoBlock
        alignItems="flex-start"
        direction="column"
        spacing={3}
        p={5}
        width={{ base: '100%', xl: '300px' }}
      >
        <Heading textStyle="h5" color="$mode.900">
          {t('Introduction')}
        </Heading>

        <SkeletonText
          height="100px"
          width="100%"
          mt={3}
          noOfLines={5}
          spacing={3}
        />
      </InfoBlock>
    );
  }
  if (!reppl?.summary) {
    return null;
  }
  /* eslint-disable */
  const textAreaRef = useRef<HTMLTextAreaElement>();
  function autoHeaigth(el) {
    el.current.style.height = '5px';
    el.current.style.height = el.current.scrollHeight + 'px';
  }
  useEffect(() => {
    autoHeaigth(textAreaRef);
  }, [autoHeaigth]);
  /* eslint-enable */

  return (
    <div style={{ width: '100%' }}>
      <Box
        h={{ base: '100%', md: 'auto' }}
        position="relative"
        alignItems={{ base: 'center', md: 'flex-start' }}
        width={{ base: '100%', xl: '300px' }}
      >
        <InfoBlock
          alignItems="flex-start"
          h="100%"
          direction="column"
          spacing={3}
          p={5}
          overflow="hidden"
        >
          <Heading textStyle="h5" color="$mode.900">
            {t('Introduction')}
          </Heading>
          <textarea
            ref={textAreaRef}
            onChange={() => {
              autoHeaigth(textAreaRef);
            }}
            disabled
            style={{
              width: '100%',
              color: '$mode.500',
              overflow: 'hidden',
              minHeight: '186px',
              resize: 'none',
              marginBottom: '32px',
              background: 'white',
              fontVariant: 'unstyled',
            }}
          >
            {reppl?.summary}
          </textarea>
        </InfoBlock>
      </Box>
    </div>
  );
}
export function BriefReppl({
  reppl,
  ...props
}: {
  reppl?: Reppl;
  loading?: boolean;
}) {
  return reppl?.isOwner ? (
    <BriefRepplOwner reppl={reppl} />
  ) : (
    <BriefRepplViewer reppl={reppl} loading={props?.loading} />
  );
}

function BriefRepplOwner({ reppl }: { reppl?: Reppl }) {
  const textAreaRef = useRef<HTMLTextAreaElement>();
  const [isEditing, setIsEditing] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutateAsync: updateReppl } = useUpdateRepplMutation(reppl?.id);
  const onSave = () => {
    if (!textAreaRef.current) return;
    setIsEditing(false);
    updateReppl({
      id: reppl?.id,
      data: {
        summary: textAreaRef.current.value,
      },
      notify: false,
    });
  };

  const { t } = useTranslation();

  const getTagLink = (data) => {
    return data.replace(/ /g, '-');
  };

  /* eslint-disable */
  const categories =
    reppl && reppl.categories.length > 0 ? reppl.categories : [];
  const [textAreaMaxLength, setTextAreaMaxLength] = useState(
    reppl?.summary.length || 0
  );
  function autoGrow(el) {
    el.current.value = el.current.value.trimStart();
    el.current.style.height = '5px';
    el.current.style.height = el.current.scrollHeight + 'px';
    setTextAreaMaxLength(el.current.value.length);
  }
  useEffect(() => {
    autoGrow(textAreaRef);
  }, [autoGrow]);
  /* eslint-enable */
  return (
    <Box minHeight="300px" position="relative">
      <InfoBlock
        variant="center"
        direction="column"
        spacing={3}
        p={5}
        width={{ base: '100%', xl: '300px' }}
      >
        <Flex w="100%" justifyContent="space-between">
          <Heading textStyle="h5" color="$mode.900">
            {t('Introduction')}
          </Heading>
          {!isEditing && reppl?.isOwner && (
            <Button
              variant="link"
              h="20px"
              fontSize="sm"
              display={{ base: 'none', md: 'block' }}
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )}
          {isEditing && reppl?.isOwner && (
            <Center>
              <Button
                variant="link"
                h="20px"
                color="$mode.500"
                fontSize="sm"
                mr={4}
                onClick={() => {
                  setIsEditing(false);

                  if (reppl?.summary !== textAreaRef.current.value) {
                    textAreaRef.current.value = reppl?.summary;
                  }
                }}
              >
                Cancel
              </Button>
              <Button variant="link" h="20px" fontSize="sm" onClick={onSave}>
                Save
              </Button>
            </Center>
          )}
        </Flex>
        {categories.length > 0 && (
          <div className="mt-5 w-full">
            {categories.map((category, key) => {
              const mkey = `key${key}`;
              if (category === '') return null;
              return (
                <Link prefetch={false} href={`/agora/${getTagLink(category)}`}>
                  <span
                    key={mkey}
                    className="truncate mr-1 px-3 py-1 text-xs bg-denim-50 text-denim-500 cursor-pointer rounded-full"
                  >
                    {category}
                  </span>
                </Link>
              );
            })}
          </div>
        )}
        <div style={{ width: '100%' }}>
          <textarea
            placeholder="Briefly describe your RE:PPL. The maximum length is 168 characters."
            ref={textAreaRef}
            text-style="body0"
            disabled={!isEditing}
            color={isEditing ? '$mode.900' : '$mode.600'}
            defaultValue={reppl?.summary ?? ''}
            style={{
              overflow: 'hidden',
              minHeight: '186px',
              resize: 'none',
              marginBottom: '32px',
              background: 'white',
              fontVariant: 'unstyled',
            }}
            onPaste={(event) => event.stopPropagation()}
            onChange={() => {
              autoGrow(textAreaRef);
            }}
            maxLength={168}
          />
          <div style={{ top: 'unset', bottom: '0' }}>
            <Text
              textStyle="labelLight"
              color="$mode.500"
              paddingRight={5}
              paddingBottom={5}
            >
              {textAreaMaxLength ?? 0}/168
            </Text>
          </div>
        </div>
      </InfoBlock>

      <Center
        position="absolute"
        bottom={0}
        width={{ base: '100%', xl: '300px' }}
        textStyle="labelLight"
        color="$mode.700"
        layerStyle="layer3"
        py="14px"
        bgColor="#fafbfb"
        border="1px solid #E6E8E9"
        borderBottomRadius="4px"
        cursor="pointer"
        onClick={onOpen}
        // _hover={{
        //   bgColor: '$mode.200',
        // }}
        _active={{
          bgColor: '$mode.300',
        }}
      >
        <SvgVector width="20px" height="20px" mr="1" />
        <span className="font-bold">Rules and Guidelines</span>
      </Center>
      <Guidelines isOpen={isOpen} onClose={onClose} reppl={reppl} />
    </Box>
  );
}
